/**
 * Support Tickets Page
 * Ticket management system implementing Support SOP
 */
// @ts-nocheck

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Heading,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Spinner,
  VStack,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Avatar,
  Tooltip,
  useColorModeValue,
  Progress,
  Tag,
  TagLabel,
  TagLeftIcon,
} from '@chakra-ui/react';
import {
  MdSearch,
  MdRefresh,
  MdMoreVert,
  MdVisibility,
  MdEdit,
  MdPriorityHigh,
  MdWarning,
  MdCheckCircle,
  MdTimer,
  MdPerson,
  MdEmail,
  MdPhone,
  MdSend,
  MdTrendingUp,
} from 'react-icons/md';
import { SupportTicket, TicketStats, TicketFilters, Feedback } from '../../types/support';
import { supportService } from '../services/supportService';

export function SupportTicketsPage() {
  const navigate = useNavigate();
  const toast = useToast();
  
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [stats, setStats] = useState<TicketStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TicketFilters>({
    search: '',
    status: 'all',
    priority: 'all',
    category: 'all',
    assignedTo: 'all',
  });

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    loadTickets();
  }, [filters]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      const response = await supportService.getTickets(filters);
      setTickets(response.tickets);
      // Compute stats from tickets since response doesn't include stats
      const computedStats: TicketStats = {
        total: response.tickets.length,
        byStatus: { open: 0, in_progress: 0, resolved: 0 },
        byPriority: { low: 0, medium: 0, high: 0 },
        avgResolutionTime: 0,
      };
      setStats(computedStats);
    } catch (error: any) {
      console.error('Error loading tickets:', error);
      toast({
        title: 'Error loading tickets',
        description: error.message || 'Please try again later',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      // Fallback to mock data on error
      setTickets(mockTickets);
      setStats(mockStats);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityColor = (priority: SupportTicket['priority']) => {
    switch (priority) {
      case 'p1': return 'red';
      case 'p2': return 'orange';
      case 'p3': return 'yellow';
      case 'p4': return 'gray';
      default: return 'gray';
    }
  };

  const getStatusColor = (status: SupportTicket['status']) => {
    switch (status) {
      case 'new': return 'blue';
      case 'assigned': return 'purple';
      case 'in_progress': return 'cyan';
      case 'pending_customer': return 'yellow';
      case 'escalated': return 'orange';
      case 'resolved': return 'green';
      case 'closed': return 'gray';
      default: return 'gray';
    }
  };

  const getPriorityLabel = (priority: SupportTicket['priority']) => {
    return priority.toUpperCase();
  };

  const getSLAStatus = (ticket: SupportTicket) => {
    const now = new Date();
    const timeToDue = ticket.firstResponseDue.getTime() - now.getTime();
    const minutesToDue = timeToDue / (1000 * 60);
    
    if (ticket.firstResponseAt) {
      return { status: 'met', color: 'green', label: 'SLA Met' };
    } else if (minutesToDue < 0) {
      return { status: 'breached', color: 'red', label: 'SLA Breached' };
    } else if (minutesToDue < 15) {
      return { status: 'critical', color: 'orange', label: `${Math.floor(minutesToDue)}m left` };
    } else {
      return { status: 'ok', color: 'green', label: `${Math.floor(minutesToDue)}m left` };
    }
  };

  const formatDateTime = (date: Date) => {
    return new Intl.DateTimeFormat('en-CA', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatDuration = (minutes: number) => {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Box>
      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={0}>
          <Heading size="lg">Support Tickets</Heading>
          <Text color="gray.600" fontSize="sm">
            Manage customer support requests
          </Text>
        </VStack>
        <HStack>
          <Button
            leftIcon={<MdRefresh />}
            onClick={loadTickets}
            variant="outline"
            isLoading={loading}
          >
            Refresh
          </Button>
          <Button colorScheme="blue" leftIcon={<MdEmail />}>
            New Ticket
          </Button>
        </HStack>
      </Flex>

      {/* Stats Cards */}
      {stats && (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 5 }} spacing={4} mb={6}>
          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Open Tickets</StatLabel>
                <StatNumber>{stats.open}</StatNumber>
                <StatHelpText>
                  {stats.total} total
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Avg Response Time</StatLabel>
                <StatNumber fontSize="lg">{formatDuration(stats.avgFirstResponseTime)}</StatNumber>
                <StatHelpText color={stats.avgFirstResponseTime < 60 ? 'green.500' : 'red.500'}>
                  Target: 1 hour
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>SLA Compliance</StatLabel>
                <StatNumber fontSize="lg">{stats.slaCompliance.toFixed(1)}%</StatNumber>
                <StatHelpText>
                  <StatArrow type={stats.slaCompliance >= 95 ? 'increase' : 'decrease'} />
                  Target: 95%
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Customer Satisfaction</StatLabel>
                <StatNumber fontSize="lg">{stats.avgCSAT.toFixed(1)}/5.0</StatNumber>
                <StatHelpText color={stats.avgCSAT >= 4.5 ? 'green.500' : 'orange.500'}>
                  Target: 4.5
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>

          <Card bg={cardBg} borderColor={borderColor}>
            <CardBody>
              <Stat>
                <StatLabel>Today's Tickets</StatLabel>
                <StatNumber>{stats.todayCount}</StatNumber>
                <StatHelpText>
                  {stats.weekCount} this week
                </StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>
      )}

      {/* Priority Distribution */}
      {stats && (
        <Card bg={cardBg} borderColor={borderColor} mb={6}>
          <CardBody>
            <Heading size="sm" mb={4}>Priority Distribution</Heading>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
              {Object.entries(stats.byPriority).map(([priority, count]) => (
                <HStack key={priority}>
                  <Badge colorScheme={getPriorityColor(priority as any)} fontSize="sm" px={2}>
                    {priority.toUpperCase()}
                  </Badge>
                  <Text fontWeight="bold">{String(count)}</Text>
                  <Text fontSize="sm" color="gray.600">tickets</Text>
                </HStack>
              ))}
            </SimpleGrid>
          </CardBody>
        </Card>
      )}

      {/* Filters */}
      <Card bg={cardBg} borderColor={borderColor} mb={6}>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4}>
            <InputGroup>
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search tickets..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </InputGroup>

            <Select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value as any })}
            >
              <option value="all">All Status</option>
              <option value="new">New</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="pending_customer">Pending Customer</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </Select>

            <Select
              value={filters.priority}
              onChange={(e) => setFilters({ ...filters, priority: e.target.value as any })}
            >
              <option value="all">All Priorities</option>
              <option value="p1">P1 - Critical</option>
              <option value="p2">P2 - High</option>
              <option value="p3">P3 - Medium</option>
              <option value="p4">P4 - Low</option>
            </Select>

            <Select
              value={filters.category}
              onChange={(e) => setFilters({ ...filters, category: e.target.value as any })}
            >
              <option value="all">All Categories</option>
              <option value="account_access">Account Access</option>
              <option value="payment_processing">Payment Processing</option>
              <option value="integrations">Integrations</option>
              <option value="onboarding">Onboarding</option>
              <option value="bug_report">Bug Report</option>
              <option value="billing">Billing</option>
            </Select>

            <Select
              value={filters.assignedTo}
              onChange={(e) => setFilters({ ...filters, assignedTo: e.target.value })}
            >
              <option value="all">All Agents</option>
              <option value="unassigned">Unassigned</option>
              <option value="me">Assigned to Me</option>
            </Select>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Tickets Table */}
      <Card bg={cardBg} borderColor={borderColor}>
        <CardHeader>
          <Flex justify="space-between" align="center">
            <Heading size="md">Tickets</Heading>
            <Text fontSize="sm" color="gray.600">
              {tickets.length} tickets
            </Text>
          </Flex>
        </CardHeader>
        <CardBody>
          {loading ? (
            <Flex justify="center" align="center" h="200px">
              <Spinner size="xl" color="blue.500" />
            </Flex>
          ) : tickets.length === 0 ? (
            <Flex justify="center" align="center" h="200px" direction="column">
              <Icon as={MdCheckCircle} boxSize={12} color="gray.400" mb={2} />
              <Text color="gray.600">No tickets found</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Ticket</Th>
                    <Th>Customer</Th>
                    <Th>Category</Th>
                    <Th>Priority</Th>
                    <Th>Status</Th>
                    <Th>Assigned To</Th>
                    <Th>SLA</Th>
                    <Th>Created</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {tickets.map((ticket) => {
                    const slaStatus = getSLAStatus(ticket);
                    return (
                      <Tr
                        key={ticket.id}
                        _hover={{ bg: hoverBg }}
                        cursor="pointer"
                        onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                      >
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="bold">{ticket.ticketNumber}</Text>
                            <Text fontSize="sm" color="gray.600" noOfLines={1}>
                              {ticket.subject}
                            </Text>
                            {ticket.language === 'fr' && (
                              <Badge colorScheme="purple" fontSize="xs">FR</Badge>
                            )}
                          </VStack>
                        </Td>
                        <Td>
                          <HStack>
                            <Avatar size="sm" name={ticket.customerName} />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">{ticket.customerName}</Text>
                              <Text fontSize="xs" color="gray.600">{ticket.customerEmail}</Text>
                            </VStack>
                          </HStack>
                        </Td>
                        <Td>
                          <Badge colorScheme="blue" fontSize="xs">
                            {ticket.category.replace('_', ' ')}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getPriorityColor(ticket.priority)} fontWeight="bold">
                            {getPriorityLabel(ticket.priority)}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(ticket.status)}>
                            {ticket.status.replace('_', ' ')}
                          </Badge>
                        </Td>
                        <Td>
                          {ticket.assignedToName ? (
                            <HStack>
                              <Avatar size="xs" name={ticket.assignedToName} />
                              <Text fontSize="sm">{ticket.assignedToName}</Text>
                            </HStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">Unassigned</Text>
                          )}
                        </Td>
                        <Td>
                          <Tooltip label={`First response ${ticket.firstResponseAt ? 'completed' : 'due'}`}>
                            <Badge colorScheme={slaStatus.color} fontSize="xs">
                              {slaStatus.label}
                            </Badge>
                          </Tooltip>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{formatDateTime(ticket.createdAt)}</Text>
                            <HStack spacing={1}>
                              <Icon
                                as={ticket.source === 'email' ? MdEmail : ticket.source === 'phone' ? MdPhone : MdSend}
                                boxSize={3}
                                color="gray.400"
                              />
                              <Text fontSize="xs" color="gray.600">{ticket.source}</Text>
                            </HStack>
                          </VStack>
                        </Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              icon={<MdMoreVert />}
                              variant="ghost"
                              size="sm"
                            />
                            <MenuList>
                              <MenuItem
                                icon={<MdVisibility />}
                                onClick={() => navigate(`/admin/support/tickets/${ticket.id}`)}
                              >
                                View Details
                              </MenuItem>
                              <MenuItem icon={<MdEdit />}>
                                Edit Ticket
                              </MenuItem>
                              <MenuItem icon={<MdPerson />}>
                                Assign to Me
                              </MenuItem>
                              <MenuItem icon={<MdPriorityHigh />}>
                                Escalate
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>
    </Box>
  );
}

// Mock Data (remove when API is ready)
const mockTickets: SupportTicket[] = [
  {
    id: 'TKT001',
    ticketNumber: 'TICKET-12345',
    customerId: 'CUS001',
    customerName: 'John Smith',
    customerEmail: 'john.smith@example.com',
    customerType: 'landlord',
    accountTier: 'Professional',
    subject: 'Cannot access Gmail integration',
    description: 'Getting OAuth error when trying to connect Gmail',
    category: 'integrations',
    priority: 'p2',
    status: 'in_progress',
    assignedTo: 'AGT001',
    assignedToName: 'Sarah Chen',
    assignedToTier: 'tier2',
    source: 'email',
    language: 'en',
    createdAt: new Date('2025-11-26T10:30:00'),
    firstResponseSLA: 60,
    firstResponseDue: new Date('2025-11-26T11:30:00'),
    firstResponseAt: new Date('2025-11-26T10:45:00'),
    resolutionSLA: 240,
    resolutionDue: new Date('2025-11-26T14:30:00'),
    responseCount: 3,
    escalationCount: 0,
    reopenCount: 0,
    updatedAt: new Date('2025-11-26T11:15:00'),
  },
  {
    id: 'TKT002',
    ticketNumber: 'TICKET-12346',
    customerId: 'CUS002',
    customerName: 'Marie Dubois',
    customerEmail: 'marie.d@example.com',
    customerType: 'landlord',
    accountTier: 'Essential',
    subject: 'Paiement non associé au locataire',
    description: 'Le paiement de novembre n\'est pas associé automatiquement',
    category: 'payment_processing',
    priority: 'p1',
    status: 'new',
    source: 'chat',
    language: 'fr',
    createdAt: new Date('2025-11-26T11:45:00'),
    firstResponseSLA: 15,
    firstResponseDue: new Date('2025-11-26T12:00:00'),
    resolutionSLA: 120,
    resolutionDue: new Date('2025-11-26T13:45:00'),
    responseCount: 0,
    escalationCount: 0,
    reopenCount: 0,
    updatedAt: new Date('2025-11-26T11:45:00'),
  },
];

const mockStats: TicketStats = {
  total: 156,
  open: 42,
  byPriority: { p1: 5, p2: 15, p3: 18, p4: 4 },
  byStatus: {
    new: 8,
    assigned: 12,
    in_progress: 15,
    pending_customer: 5,
    escalated: 2,
    resolved: 98,
    closed: 16,
  },
  byCategory: {
    account_access: 12,
    payment_processing: 25,
    integrations: 18,
    onboarding: 15,
    feature_request: 8,
    bug_report: 10,
    data_reporting: 6,
    billing: 4,
    other: 2,
  },
  avgFirstResponseTime: 45,
  avgResolutionTime: 180,
  slaBreach: 8,
  slaCompliance: 94.9,
  avgCSAT: 4.6,
  escalationRate: 4.8,
  reopenRate: 3.2,
  todayCount: 12,
  weekCount: 48,
  monthCount: 156,
};
